/** Which agents a template reads, shown the way the app shows agents: each
 *  with its avatar (BotAvatar) and name. The chosen ones are chips above (a
 *  chip's × takes it out), then search, Select all / Clear, how many are
 *  chosen, and the list with the app's checkbox. Agents that share a name
 *  are one choice (the Insights filters group them so); the row says how
 *  many agents it stands for. */
import { useMemo, useState } from 'react';
import { Checkbox, Input } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { BotAvatar } from '@/components/common/BotAvatar';
import type { AgentOption } from '../../types/insights';

interface Props {
  options: AgentOption[];
  /** The chosen options' values (each a comma-joined list of agent ids). */
  value: string[];
  onChange: (values: string[]) => void;
  label: string;
}

export function AgentPicker({ options, value, onChange, label }: Props) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const chosen = useMemo(() => new Set(value), [value]);
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const shown = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return q ? options.filter((o) => o.label.toLocaleLowerCase().includes(q)) : options;
  }, [options, query]);
  const allShownChosen = shown.length > 0 && shown.every((o) => chosen.has(o.value));
  const flip = (v: string) => onChange(chosen.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  const avatar = (o: AgentOption, size: number) => (
    <BotAvatar name={o.label} size={size} colorOverride={o.avatarColor || undefined}
               variantOverride={o.avatarVariant ?? undefined} title="" />
  );

  return (
    <div className="tpl-picker" data-testid="template-agent-picker">
      {value.length > 0 && (
        <ul className="tpl-agent-chips" aria-label={t('insights.tpl.editor.agentsChosenList')}>
          {value.map((v) => byValue.get(v)).filter((o): o is AgentOption => !!o).map((o) => (
            <li key={o.value} className="tpl-agent-chip">
              {avatar(o, 20)}
              <bdi dir="auto" className="tpl-agent-chip__name">{o.label}</bdi>
              <button type="button" className="tpl-icon-btn" onClick={() => flip(o.value)}
                      aria-label={t('insights.tpl.editor.removeAgent', { name: o.label })}>
                <CloseOutlined aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="cxl-orgs__tools">
        <Input className="cxl-orgs__search" size="small" allowClear dir="auto" value={query}
               prefix={<SearchOutlined aria-hidden />} onChange={(e) => setQuery(e.target.value)}
               placeholder={t('insights.tpl.editor.searchAgents')} aria-label={t('insights.tpl.editor.searchAgents')} />
        <button type="button" className="cx-btn cx-btn--sm" disabled={allShownChosen}
                onClick={() => onChange([...value, ...shown.map((o) => o.value).filter((v) => !chosen.has(v))])}>
          {query.trim() ? t('insights.tpl.editor.selectShown') : t('connectorLibrary.orgs.selectAll')}
        </button>
        <button type="button" className="cx-btn cx-btn--sm" onClick={() => onChange([])} disabled={!value.length}>
          {t('connectorLibrary.orgs.clear')}
        </button>
      </div>
      <p className="tpl-picker__count" aria-live="polite" data-testid="template-agent-count">
        {t('insights.tpl.editor.agentsChosen', { count: value.length, total: options.length })}
      </p>
      {shown.length === 0 ? (
        <p className="tpl-muted">{t('insights.tpl.editor.noAgentMatch')}</p>
      ) : (
        <ul className="tpl-picker__list tpl-agent-list" role="listbox" aria-label={label} aria-multiselectable="true">
          {shown.map((o) => {
            const ids = o.value.split(',').length;
            return (
              <li key={o.value} role="option" aria-selected={chosen.has(o.value)} className="tpl-agent-row">
                <Checkbox checked={chosen.has(o.value)} onChange={() => flip(o.value)} className="tpl-agent-row__check"
                          data-testid={`template-agent-${o.value}`}>
                  <span className="tpl-agent-row__who">
                    {avatar(o, 24)}
                    <bdi dir="auto" className="tpl-agent-row__name">{o.label}</bdi>
                    {ids > 1 && <span className="tpl-agent-row__many">{t('insights.tpl.editor.sameName', { count: ids })}</span>}
                  </span>
                </Checkbox>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
